import React from 'react';
import {Link, useLocation} from 'react-router-dom';
import {useAuth} from '../../contexts/AuthContext';
import {usePhase} from '../../contexts/PhaseContext';

const Layout = ({children}) => {
    const {user, logout} = useAuth();
    const {phase, isPhaseAllowed} = usePhase();
    const location = useLocation();

    const handleLogout = () => {
        logout();
    };

    const isActive = (path) => {
        return location.pathname === path;
    };

    const isAdminPath = () => {
        return location.pathname.startsWith('/admin');
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <nav className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center">
                            <Link to="/" className="text-2xl font-bold text-blue-600">
                                ArtSwap
                            </Link>
                        </div>

                        <div className="flex items-center space-x-4">
                            {user ? (
                                <>
                                    {/* Main User Navigation */}
                                    <div className="hidden md:flex items-center space-x-1">
                                        <Link
                                            to="/dashboard"
                                            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                                isActive('/dashboard') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:text-blue-600'
                                            }`}
                                        >
                                            Dashboard
                                        </Link>

                                        <Link
                                            to="/upload"
                                            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                                isActive('/upload')
                                                    ? 'bg-blue-100 text-blue-700'
                                                    : !isPhaseAllowed('upload')
                                                    ? 'text-gray-400 cursor-not-allowed'
                                                    : 'text-gray-700 hover:text-blue-600'
                                            }`}
                                            title={!isPhaseAllowed('upload') ? 'Upload only available during Uploading phase' : ''}
                                        >
                                            Upload
                                        </Link>

                                        <Link
                                            to="/my-art"
                                            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                                isActive('/my-art') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:text-blue-600'
                                            }`}
                                        >
                                            My Art
                                        </Link>

                                        <Link
                                            to="/voting"
                                            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                                isActive('/voting')
                                                    ? 'bg-blue-100 text-blue-700'
                                                    : !isPhaseAllowed('vote')
                                                    ? 'text-gray-400 cursor-not-allowed'
                                                    : 'text-gray-700 hover:text-blue-600'
                                            }`}
                                            title={!isPhaseAllowed('vote') ? 'Voting only available during Voting phase' : ''}
                                        >
                                            Voting
                                        </Link>

                                        <Link
                                            to="/matches"
                                            className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                                isActive('/matches') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:text-blue-600'
                                            }`}
                                        >
                                            Matches
                                        </Link>
                                    </div>

                                    {/* Mobile Menu */}
                                    <div className="md:hidden relative group">
                                        <button className="px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-blue-600">Menu ▼</button>
                                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                                            <Link
                                                to="/dashboard"
                                                className={`block px-4 py-2 text-sm transition-colors ${
                                                    isActive('/dashboard') ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                Dashboard
                                            </Link>
                                            <Link
                                                to="/upload"
                                                className={`block px-4 py-2 text-sm transition-colors ${
                                                    isActive('/upload')
                                                        ? 'bg-blue-50 text-blue-700'
                                                        : !isPhaseAllowed('upload')
                                                        ? 'text-gray-400'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                Upload {!isPhaseAllowed('upload') && '(Disabled)'}
                                            </Link>
                                            <Link
                                                to="/my-art"
                                                className={`block px-4 py-2 text-sm transition-colors ${
                                                    isActive('/my-art') ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                My Art
                                            </Link>
                                            <Link
                                                to="/voting"
                                                className={`block px-4 py-2 text-sm transition-colors ${
                                                    isActive('/voting')
                                                        ? 'bg-blue-50 text-blue-700'
                                                        : !isPhaseAllowed('vote')
                                                        ? 'text-gray-400'
                                                        : 'text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                Voting {!isPhaseAllowed('vote') && '(Disabled)'}
                                            </Link>
                                            <Link
                                                to="/matches"
                                                className={`block px-4 py-2 text-sm transition-colors ${
                                                    isActive('/matches') ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                Matches
                                            </Link>
                                            <Link
                                                to="/profile"
                                                className={`block px-4 py-2 text-sm transition-colors ${
                                                    isActive('/profile') ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                Profile
                                            </Link>
                                            <Link
                                                to="/settings"
                                                className={`block px-4 py-2 text-sm transition-colors ${
                                                    isActive('/settings') ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                Settings
                                            </Link>
                                        </div>
                                    </div>

                                    {/* Admin Dropdown */}
                                    {user.isAdmin && (
                                        <div className="relative group">
                                            <button
                                                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                                    isAdminPath() ? 'bg-red-100 text-red-700' : 'text-red-700 hover:text-red-600 bg-red-50'
                                                }`}
                                            >
                                                Admin ▼
                                            </button>
                                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                                                <Link
                                                    to="/admin/phases"
                                                    className={`block px-4 py-2 text-sm transition-colors ${
                                                        isActive('/admin/phases') ? 'bg-red-50 text-red-700' : 'text-gray-700 hover:bg-gray-100'
                                                    }`}
                                                >
                                                    Phase Management
                                                </Link>
                                                <Link
                                                    to="/admin/invitations"
                                                    className={`block px-4 py-2 text-sm transition-colors ${
                                                        isActive('/admin/invitations') ? 'bg-red-50 text-red-700' : 'text-gray-700 hover:bg-gray-100'
                                                    }`}
                                                >
                                                    Invitations
                                                </Link>
                                            </div>
                                        </div>
                                    )}

                                    {/* User Profile Dropdown */}
                                    <div className="relative group">
                                        <button className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">
                                            <span className="hidden sm:block">
                                                {user.firstName} {user.lastName}
                                                {user.isAdmin && <span className="ml-1 text-xs bg-red-100 text-red-800 px-1 rounded">Admin</span>}
                                            </span>
                                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                                <span className="text-sm font-semibold text-blue-600">
                                                    {user.firstName.charAt(0)}
                                                    {user.lastName.charAt(0)}
                                                </span>
                                            </div>
                                        </button>
                                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                                            <Link
                                                to="/profile"
                                                className={`block px-4 py-2 text-sm transition-colors ${
                                                    isActive('/profile') ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                Profile
                                            </Link>
                                            <Link
                                                to="/settings"
                                                className={`block px-4 py-2 text-sm transition-colors ${
                                                    isActive('/settings') ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                                                }`}
                                            >
                                                Settings
                                            </Link>
                                            <hr className="my-1" />
                                            <button
                                                onClick={handleLogout}
                                                className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 transition-colors"
                                            >
                                                Logout
                                            </button>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <Link to="/login" className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">
                                        Login
                                    </Link>
                                    <Link
                                        to="/register"
                                        className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                                    >
                                        Register
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            <main>{children}</main>
        </div>
    );
};

export default Layout;
export {Layout};
